import { Injectable } from '@angular/core';
import { Util } from '@phdsoft/common';
import * as THREE from '@phdsoft/three';
import { PerspectiveCamera } from '@phdsoft/three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

@Injectable({ providedIn: 'root' })
export class CameraControl {

    private camera: THREE.Camera = null;
    private controls: OrbitControls = null;

    // -----------------------------------------------------------------------
    // Camera animation state (Phase 3 -- SGR-01)
    // -----------------------------------------------------------------------

    /** Active requestAnimationFrame ID, or null if no animation is running. */
    private _animationFrameId: number | null = null;

    /** Whether the user is currently interacting with OrbitControls (mid-drag). */
    private _isUserInteracting: boolean = false;

    /** Queued camera restore to execute when user finishes mid-orbit interaction. */
    private _pendingCameraRestore: { position: THREE.Vector3; target: THREE.Vector3 } | null = null;

    /** Bound handler references for cleanup. */
    private _onControlStart: (() => void) | null = null;
    private _onControlEnd: (() => void) | null = null;

    public setParameters(controls: OrbitControls, camera: THREE.Camera): void {
        this.controls = controls;
        this.camera = camera;
        this.setupInteractionTracking(controls);
    }

    constructor() {}

    public destroy(): void {
      // Cancel any running animation
      if (this._animationFrameId !== null) {
          cancelAnimationFrame(this._animationFrameId);
          this._animationFrameId = null;
      }

      // Clear pending restore
      this._pendingCameraRestore = null;
      this._isUserInteracting = false;

      // Remove interaction tracking listeners
      if (this.controls && this._onControlStart && this._onControlEnd) {
          this.controls.removeEventListener('start', this._onControlStart as any);
          this.controls.removeEventListener('end', this._onControlEnd as any);
      }
      this._onControlStart = null;
      this._onControlEnd = null;

      // The camera and controls are not created or owned by this service,
      // so they should not be disposed of here. Their lifecycle is managed
      // by the service or component that created them.
      this.camera = null;
      this.controls = null;
    }

    // -----------------------------------------------------------------------
    // Smooth camera animation (Phase 3 -- SGR-01)
    // -----------------------------------------------------------------------

    /**
     * Smoothly interpolates the camera from its current position/target to
     * the specified target position/target over the given duration.
     *
     * Uses requestAnimationFrame with ease-out quadratic easing:
     *   eased = t * (2 - t)
     *
     * Cancels any previously running animation before starting.
     *
     * @param targetPosition The camera position to animate to.
     * @param targetTarget The orbit controls target to animate to.
     * @param durationMs Duration of the animation in milliseconds (default: 300).
     */
    public animateCameraToTarget(
        targetPosition: THREE.Vector3,
        targetTarget: THREE.Vector3,
        durationMs: number = 300,
    ): void {
        if (!this.camera || !this.controls) return;

        // Cancel any existing animation
        if (this._animationFrameId !== null) {
            cancelAnimationFrame(this._animationFrameId);
            this._animationFrameId = null;
        }

        const startPos = this.camera.position.clone();
        const startTarget = this.controls.target.clone();
        const startTime = performance.now();

        const animate = () => {
            const elapsed = performance.now() - startTime;
            const t = Math.min(elapsed / durationMs, 1.0);
            const eased = t * (2 - t); // ease-out quadratic

            this.camera.position.lerpVectors(startPos, targetPosition, eased);
            this.controls.target.lerpVectors(startTarget, targetTarget, eased);
            this.controls.update();

            if (t < 1.0) {
                this._animationFrameId = requestAnimationFrame(animate);
            } else {
                this._animationFrameId = null;
            }
        };

        this._animationFrameId = requestAnimationFrame(animate);
    }

    /**
     * Restores the camera to the given position/target with smooth animation.
     *
     * If the user is currently mid-orbit (dragging), the restore is queued
     * and will execute when the user releases. This prevents jarring camera
     * jumps during active manipulation.
     *
     * @param targetPosition The camera position to restore to.
     * @param targetTarget The orbit controls target to restore to.
     */
    public restoreCameraSmooth(targetPosition: THREE.Vector3, targetTarget: THREE.Vector3): void {
        if (this._isUserInteracting) {
            // Queue the restore until user finishes interacting
            this._pendingCameraRestore = {
                position: targetPosition.clone(),
                target: targetTarget.clone(),
            };
            return;
        }

        this.animateCameraToTarget(targetPosition, targetTarget);
    }

    /**
     * Immediately cancels any running camera animation and clears any
     * queued pending restore. Called by AssetStateManager before applying
     * a new undo/redo target state to prevent stale animation completion.
     */
    public cancelAnimation(): void {
        if (this._animationFrameId !== null) {
            cancelAnimationFrame(this._animationFrameId);
            this._animationFrameId = null;
        }
        this._pendingCameraRestore = null;
    }

    // -----------------------------------------------------------------------
    // Private: Interaction tracking
    // -----------------------------------------------------------------------

    /**
     * Sets up listeners on OrbitControls 'start' and 'end' events to track
     * whether the user is mid-interaction (dragging to orbit/pan/zoom).
     *
     * When the user finishes interacting and there is a pending camera restore,
     * it is executed automatically.
     */
    private setupInteractionTracking(controls: OrbitControls): void {
        // Clean up previous listeners if setParameters is called again
        if (this._onControlStart && this._onControlEnd && this.controls) {
            this.controls.removeEventListener('start', this._onControlStart as any);
            this.controls.removeEventListener('end', this._onControlEnd as any);
        }

        this._onControlStart = () => {
            this._isUserInteracting = true;
        };

        this._onControlEnd = () => {
            this._isUserInteracting = false;

            // Execute any pending camera restore
            if (this._pendingCameraRestore) {
                const pending = this._pendingCameraRestore;
                this._pendingCameraRestore = null;
                this.animateCameraToTarget(pending.position, pending.target);
            }
        };

        controls.addEventListener('start', this._onControlStart as any);
        controls.addEventListener('end', this._onControlEnd as any);
    }

    public setCameraAndTarget(camperaPosition: THREE.Vector3, target: THREE.Vector3): void {
        this.controls.target.copy(target.clone());
        this.camera.position.copy(camperaPosition.clone());
    }


    public setCameraPosition(bBox: THREE.Box3) {
        if (this.controls) {
            if (!bBox) {
                console.error("bBox is not defined.");
                return;
            }
            bBox.getCenter(this.controls.target);
            const xDif = bBox.max.x - bBox.min.x;
            const yDif = bBox.max.y - bBox.min.y;
            const zDif = bBox.max.z - bBox.min.z;
            const radius = Math.max(xDif, yDif, zDif);
            const normalArray = new THREE.Vector3(0, 0, 0);
            const nx = normalArray.x - 0.8;
            const ny = normalArray.y + 0.15;
            const nz = normalArray.z + 0.5;
            const x = this.controls.target.x + nx * (radius * 1.5);
            const y = this.controls.target.y + ny * (radius * 1.5);
            const z = this.controls.target.z + nz * (radius * 1.5);

            this.camera.position.copy(new THREE.Vector3(x, y, z));
        }
    }

    public setCameraZeroPosition() {
        this.camera.position.copy(new THREE.Vector3(0, 0, 0));

    }

    public setCameraForSection(bBox: THREE.Box3, _normalArray: THREE.Vector3, lastCameraSignal: number) {
        // const normalCopy = new THREE.Vector3(_normalArray.x, _normalArray.y, _normalArray.z);
        const value = Util.LargestCanonicalAxisValue(_normalArray);

        const canonicalNormal = new THREE.Vector3(value[0], value[1], value[2]);
        const normalCopy = canonicalNormal.clone();
        const normalArray = lastCameraSignal >= 0 ? normalCopy : normalCopy.multiplyScalar(-1);
        if (this.controls) {
            bBox.getCenter(this.controls.target);
            const xDif = bBox.max.x - bBox.min.x;
            const yDif = bBox.max.y - bBox.min.y;
            const zDif = bBox.max.z - bBox.min.z;
            const radius = Math.max(xDif, yDif, zDif);
            if (normalArray != undefined && normalArray != null) {
                const nx = normalArray.x;
                const ny = normalArray.y;
                const nz = normalArray.z;

                const px = this.controls.target.x + nx * (radius * 1.5);
                const py = this.controls.target.y + ny * (radius * 1.5);
                const pz = this.controls.target.z + nz * (radius * 1.5);

                this.camera.position.set(px, py, pz);
            }
        }
    }


    public setcanonicalCameraForSection(bBox, _normalArray) {
        const value = Util.LargestCanonicalAxisValue(_normalArray);
        const canonicalNormal = new THREE.Vector3(value[0], value[1], value[2]);
        const normalArray = canonicalNormal.clone();
        if (this.controls) {
            bBox.getCenter(this.controls.target);
            const radius = Math.max(bBox.max.x - bBox.min.x, bBox.max.y - bBox.min.y, bBox.max.z - bBox.min.z);
            if (normalArray != undefined && normalArray != null) {
                const px = this.controls.target.x + normalArray.x * (radius * 1.5);
                const py = this.controls.target.y + normalArray.y * (radius * 1.5);
                const pz = this.controls.target.z + normalArray.z * (radius * 1.5);

                this.camera.position.set(px, py, pz);
            }
        }
    }

    public zoomFitObject(bBox: THREE.Box3, normal: THREE.Vector3, radius?: number) {
        let normalArray = normal.clone();
        if (this.controls) {
            bBox.getCenter(this.controls.target);
            radius = radius || Math.max(bBox.max.x - bBox.min.x, bBox.max.y - bBox.min.y, bBox.max.z - bBox.min.z);
            if (normalArray != undefined && normalArray != null && (this.camera instanceof PerspectiveCamera)) {
                normalArray = normalArray.multiplyScalar(radius * (1 / (this.camera as PerspectiveCamera).aspect));
                const vectorSide1 = this.controls.target.clone().add(normalArray.clone());
                const vectorSide2 = this.controls.target.clone().add(normalArray.clone().multiplyScalar(-1));
                if (this.camera.position.distanceTo(vectorSide1) < this.camera.position.distanceTo(vectorSide2)) {
                    this.camera.position.copy(vectorSide1);
                }
                else {
                    this.camera.position.copy(vectorSide2);
                }
            }

        }
    }
}
